import React, { useEffect, useState } from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import { Icon } from '../../components';
import Theme from '../../theme';
import { LoadingModal, SliderGauge } from '../../ui';
import { defaultScale } from '../../utils/scaling';
import { getMealsForTimeOfDay, setMaximumMealPrepTime } from '../actions';
import { availableTimesSelector, selectAndSortMealAccordingToMaximumMealPrepTime, selectMinAndMaxPrepTimes,
selectSliderStartingPoint } from '../selectors';
import { MealSelectionCard } from './MealSelectionCard';

interface IChangeMealProps {
  toggleMealSelectionWizardScreen: () => void;
  mealOfDay: string;
  isLoadingMealsForTimeOfDay: boolean;
  mealsForTimeOfDay: any;
  mealsForTimeOfDayFiltered: any;
  nutritionService: any;
  mealPlanId: string;
  modalTitle: string;
  minPrepTime: number;
  maxPrepTime: number;
  currentValue: number;
  sliderStartingPoint: number;
  availableTimes: number[];
  initialTime: number;
  maximumMealPrepTimeChanged: (maximumMealPrepTime: number) => void;
  mealChanged: (mealId: string) => void;
  fetchMealsForTimeOfDay: (
    nutritionService: any,
    mealOfDay: string,
    mealPlanId: string,
  ) => void;
  t: (str: string, object?: { [key: string]: string|number }) => string;
}
const MealSelectionComponent = ({
  toggleMealSelectionWizardScreen,
  mealsForTimeOfDay,
  mealsForTimeOfDayFiltered,
  isLoadingMealsForTimeOfDay,
  mealOfDay,
  mealPlanId,
  nutritionService,
  fetchMealsForTimeOfDay,
  mealChanged,
  modalTitle,
  minPrepTime,
  maxPrepTime,
  maximumMealPrepTimeChanged,
  sliderStartingPoint,
  initialTime,
  t,
  availableTimes,
}: IChangeMealProps) => {
  const [maxMinsDisplay, setMaxMinsDisplay] = useState(initialTime);
  const shouldShowSlider = minPrepTime && maxPrepTime;
  useEffect(() => {
    if (mealsForTimeOfDay[mealOfDay] === undefined) {
      if (!isLoadingMealsForTimeOfDay) {
        fetchMealsForTimeOfDay(nutritionService, mealOfDay, mealPlanId);
      }
    }
  });

  useEffect(() => {
      if (shouldShowSlider ) {
        setMaxMinsDisplay(initialTime);
        maximumMealPrepTimeChanged(initialTime);
      }
  }, [shouldShowSlider]);

  if (mealsForTimeOfDay[mealOfDay] === undefined || isLoadingMealsForTimeOfDay) {
    return <LoadingModal style={styles.loading} />;
  }
  return (
    <View style={{ flex: 1, backgroundColor: Theme.GREY_FIGMA}}>
      <View style={styles.headingContainerStyle}>
        <TouchableOpacity onPress={toggleMealSelectionWizardScreen}>
          <Icon
            size={defaultScale(30)}
            color={Theme.GREY}
            name='ios-arrow-back'
            style={styles.iconContainerStyle}
          />
        </TouchableOpacity>
        <View style={styles.headerPadding}>
          <Text style={styles.modalTitle}>{modalTitle}</Text>
          <View style={styles.prepTimeTextContainer}>
            <Text style={styles.prepTimeText}>{t('setMaximumPrepTime')}</Text>
            {shouldShowSlider && <Text
              style={[Theme.textStyle.bodyCopyBold]}
            >
              {t('maxPrepTime', {mins: maxMinsDisplay})}
            </Text>}
          </View>
          {shouldShowSlider &&
            <SliderGauge
              style={styles.sliderStyle}
              onSlidingComplete={(value) => {
                const selectedTime = availableTimes[value];
                maximumMealPrepTimeChanged(selectedTime);
              }}
              onValueChange={(value) => {
                const selectedTime = availableTimes[value];
                setMaxMinsDisplay(selectedTime);
              }}
              initialValue={sliderStartingPoint}
              minimumValue={0}
              maximumValue={availableTimes.length - 1}
              step={1}
              minimumTrackTintColor='#AAAAAA'
              maximumTrackTintColor='#AAAAAA'
            />
          }
        </View>
      </View>
      <ScrollView>
        { mealsForTimeOfDayFiltered.length > 0 && mealsForTimeOfDayFiltered.map(
          (
            {
              calories,
              carbs,
              fat,
              protein,
              displayName,
              urlForDesktopImage,
              prepDuration,
              _id,
            },
            index,
          ) => {
            return (
              <MealSelectionCard
                urlForDesktopImage={urlForDesktopImage}
                displayName={displayName}
                carbs={carbs}
                cals={calories}
                fat={fat}
                protein={protein}
                prepTime={prepDuration}
                t={t}
                hasMarginTop={index !== 0}
                mealChanged={() => {
                  mealChanged(_id);
                  toggleMealSelectionWizardScreen();
                }}
              />
            );
          },
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    justifyContent: 'center',
  },
  iconContainerStyle: {
    paddingLeft: defaultScale(20),
    paddingTop: defaultScale(10),
  },
  modalTitle: {
    ...Theme.textStyle.heading,
    paddingBottom: defaultScale(22),
    marginTop: defaultScale(10),
  },
  headingContainerStyle: {
    backgroundColor: Theme.WHITE,
    marginBottom: defaultScale(15),
  },
  headerPadding: {
    marginHorizontal: defaultScale(10),
  },
  prepTimeText: {
    ...Theme.textStyle.bodyCopyReg,
    marginBottom: defaultScale(10),
  },
  prepTimeTextContainer: {
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  sliderStyle: {
    width: '102%',
    marginBottom: defaultScale(10),
    marginLeft: defaultScale(-2),
  },
});

const mapDispatchToProps = (dispatch) => {
  return {
    fetchMealsForTimeOfDay: (nutritionService, mealOfDay, mealPlanId) =>
      dispatch(getMealsForTimeOfDay(nutritionService, mealOfDay, mealPlanId)),
    maximumMealPrepTimeChanged: (maximumMealPrepTime) => {
      dispatch(setMaximumMealPrepTime(maximumMealPrepTime));
    },
  };
};

const mapStateToProps = (state) => {
  const { minPrepTime, maxPrepTime } = selectMinAndMaxPrepTimes(state);
  const availableTimes = availableTimesSelector(state);
  const sliderStartingPointMinutes =  selectSliderStartingPoint(state);
  const closest = availableTimes.reduce((prev: number, curr: number) => {
    return (Math.abs(curr - sliderStartingPointMinutes) < Math.abs(prev - sliderStartingPointMinutes) ? curr : prev);
  });
  const sliderStartingPoint = availableTimes.findIndex((time: number) => time === closest);
  const initialTime = availableTimes[sliderStartingPoint];
  return {
    mealsForTimeOfDay: state.nutrition.mealsForTimeOfDay,
    mealsForTimeOfDayFiltered:
      selectAndSortMealAccordingToMaximumMealPrepTime(state),
    isLoadingMealsForTimeOfDay: state.nutrition.isLoadingMealsForTimeOfDay,
    sliderStartingPoint,
    minPrepTime,
    maxPrepTime,
    availableTimes,
    initialTime,
  };
};

export const MealSelection = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MealSelectionComponent);
